import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import api from "../lib/axios";
import { clearOfflineSales, getOfflineSales } from "../lib/offlineSales";
import type { SaleRecordExpanded } from "../types/sales";
import type { RootState } from "../store";

// ======================
// TYPE DEFINITIONS
// ======================

export interface ItemPurchase {
  id?: string;
  sale_id?: string;
  product_id: string;
  quantity: number;
}

export interface Sale {
  id?: string;
  customer_id?: string;
  store_id?: string;
  business_name?: string;
  amount?: string;
  cashier_id?: string;
  payment_method: string;
  created_at?: string;
  ItemPurchase?: ItemPurchase[];
}

// export interface CreateSaleRequest {
//   customer_id?: string;
//   store_id?: string;
//   cashier_id?: string;
//   payment_method: string;
//   items: {
//     product_id: string;
//     quantity: number;
//   }[];
// }

interface SaleItem {
  product_id: string;
  quantity: number;
}

interface NewCustomer {
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
}

interface SalePayload {
  cashier_id: string;
  store_id: string;
  business_name: string;
  payment_method: string;
  amount_received: number | string;
  customer_id?: string | null;
  new_customer?: NewCustomer | null;
  items: SaleItem[];
}

interface CreateSaleRequest {
  // customer_id?: string | null;
  // new_customer?: NewCustomer | null;
  sale: SalePayload;
}

interface CreateSaleResponse {
  message: string;
  data: any;
}

export interface SalesHistoryFilters {
  storeId?: string | null;
  productId?: string | null;
  startDate?: string | null; // ISO
  endDate?: string | null;   // ISO
}


export interface SalesState {
  sales: Sale[];
  currentSale?: Sale;
  loading: boolean;
  error?: string;
  successMessage?: string;syncing: boolean;
  syncMessage: string | null;
  history: SaleRecordExpanded[];
  historyFilters: SalesHistoryFilters | null;
}

// ======================
// INITIAL STATE
// ======================

const initialState: SalesState = {
  sales: [],
  loading: false,
  error: undefined,
  successMessage: undefined,
  syncing: false,
  syncMessage: null,
  history: [],
  historyFilters: null
};

// ======================
// ASYNC THUNKS
// ======================

// Create a new sale
export const createSale = createAsyncThunk<
  Sale,
  CreateSaleRequest,
  { rejectValue: string }
>("sales/createSale", async (saleData, { rejectWithValue }) => {
  try {
    const response = await api.post(
      `/sale/add`,
      saleData
    );
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to create sale"
    );
  }
});

// Fetch all sales
export const fetchSales = createAsyncThunk<
  Sale[],
  void,
  { rejectValue: string }
>("sales/fetchSales", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(`/sale/all`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to fetch sales"
    );
  }
});

export const createSaleWithReceipt = createAsyncThunk<
  CreateSaleResponse,
  CreateSaleRequest,
  { rejectValue: string }
>("sales/createSaleWithReceipt", async (payload, { rejectWithValue }) => {
  try {
    const response = await api.post<CreateSaleResponse>(`/sale/add-with-receipt`, payload);

    return response.data;
  } catch (error: any) {
    console.error("Sale creation failed:", error);
    const errorMsg =
      error.response?.data?.message || "An error occurred while creating the sale.";
    return rejectWithValue(errorMsg);
  }
});

export const syncOfflineSales = createAsyncThunk(
  "sales/syncOfflineSales",
  async (_, { rejectWithValue }) => {
    try {
      const offlineSales = await getOfflineSales();
      if (offlineSales.length === 0) {
        console.log("No offline sales to sync");
        return { message: "No offline sales to sync." };
      }

      const responses = [];
      for (const sale of offlineSales) {
        const res = await api.post(`/sale/add`, sale);
        responses.push(res.data);
      }

      await clearOfflineSales();
      return { message: "Offline sales synced successfully.", responses };
    } catch (error: any) {
      return rejectWithValue("Failed to sync offline sales.");
    }
  }
);

export const fetchSalesHistory = createAsyncThunk<
  SaleRecordExpanded[],
  void,
  { state: RootState, rejectWithValue: String }
>("sales/getSalesHistory", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const filters = state.sales.historyFilters;

    const params = new URLSearchParams();

    if (filters?.storeId) {
      params.append("storeId", filters.storeId);
    }

    if (filters?.productId) {
      params.append("productId", filters.productId);
    }

    if (filters?.startDate) {
      params.append("startDate", filters.startDate);
    }

    if (filters?.endDate) {
      params.append("endDate", filters.endDate);
    }

    const queryString = params.toString();
    const url = queryString
      ? `/sale/history?${queryString}`
      : `/sale/history`;

    const { data } = await api.get(url);

    return data.data as SaleRecordExpanded[];

  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch sales history")
  }
});

// ======================
// SLICE
// ======================

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = undefined;
      state.successMessage = undefined;
    },
    updateHistoryFilters: (state, action: PayloadAction<SalesHistoryFilters>) => {
      state.historyFilters = action.payload;
    }
  },
  extraReducers: (builder) => {
    // CREATE SALE
    builder
      .addCase(createSale.pending, (state) => {
        state.loading = true;
        state.error = undefined;
        state.successMessage = undefined;
      })
      .addCase(createSale.fulfilled, (state, action: PayloadAction<Sale>) => {
        state.loading = false;
        state.sales.push(action.payload);
        state.currentSale = action.payload;
        state.successMessage = "Sale created successfully!";
      })
      .addCase(createSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create sale.";
      });

    // FETCH SALES
    builder
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchSales.fulfilled, (state, action: PayloadAction<Sale[]>) => {
        state.loading = false;
        state.sales = action.payload;
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch sales.";
      });

      // CREATE NEW SALE WITH RECEIPT
    builder
      .addCase(createSaleWithReceipt.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(createSaleWithReceipt.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.sales.push(action.payload.data);
      })
      .addCase(createSaleWithReceipt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create sale";
      });

      // SYNC OFFLINE SALES
      builder
      .addCase(syncOfflineSales.pending, (state) => {
        state.syncing = true;
        state.syncMessage = null;
      })
      .addCase(syncOfflineSales.fulfilled, (state, action) => {
        state.syncing = false;
        state.syncMessage = action.payload.message;
      })
      .addCase(syncOfflineSales.rejected, (state) => {
        state.syncing = false;
        state.syncMessage = "Sync failed.";
      });

      // GET SALES HISTORY
      builder
        .addCase(fetchSalesHistory.pending, (state) => {
          state.loading = true;
        })
        .addCase(fetchSalesHistory.fulfilled, (state, action) => {
          state.loading = false;
          state.history = action.payload;
        })
         .addCase(fetchSalesHistory.rejected, (state, action) => {
          state.loading = false;
          console.log("Error: ", action.payload);
          state.error = "Failed to fetch sales history. Please try again";
        })
  },
});

// ======================
// EXPORTS
// ======================

export const { clearMessages, updateHistoryFilters } = salesSlice.actions;
export const salesReducer = salesSlice.reducer;
